import React, {useEffect, useMemo, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {Calendar} from "primereact/calendar";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {Chart} from "primereact/chart";
import {useTranslate} from "../../system/useTranslate";

const EMPTY_VALUE = "__empty__";

const chartColors = [
    "#78cde0",
    "#b33110",
    "#03777a",
    "#ffcfbd",
    "#6d7684",
    "#f1a58d",
    "#4dd6e5",
    "#7f1d0a",
];

const entityConfigs = {
    task: {
        labelKey: "reports.builder.entity.task",
        rowsKey: "tasks",
        defaultXAxis: "project",
        defaultMetric: "taskCount",
        defaultDateField: "createdAt",
        sources: ["workspace", "myWork", "projectOwner", "specificProject", "portfolio"],
        dateFields: ["createdAt", "updatedAt", "dueDate", "doneAt"],
        dimensions: ["assignee", "project", "taskStatus", "dueStatus", "taskType", "date"],
        filterFields: ["assignee", "project", "taskStatus", "dueStatus", "taskType", "date"],
        metrics: ["taskCount", "completedTasks", "incompleteTasks", "timeToComplete"],
    },
    project: {
        labelKey: "reports.builder.entity.project",
        rowsKey: "projects",
        defaultXAxis: "owner",
        defaultMetric: "projectCount",
        defaultDateField: "createdAt",
        sources: ["workspace", "projectOwner", "specificProject", "portfolio"],
        dateFields: ["createdAt", "updatedAt", "dueDate"],
        dimensions: ["owner", "projectStatus", "portfolio", "goal", "date"],
        filterFields: ["owner", "projectStatus", "portfolio", "goal", "date"],
        metrics: ["projectCount"],
    },
    portfolio: {
        labelKey: "reports.builder.entity.portfolio",
        rowsKey: "portfolios",
        defaultXAxis: "owner",
        defaultMetric: "portfolioCount",
        defaultDateField: "createdAt",
        sources: ["workspace", "portfolioOwner", "specificPortfolio"],
        dateFields: ["createdAt", "updatedAt"],
        dimensions: ["owner", "portfolio", "date"],
        filterFields: ["owner", "portfolio", "date"],
        metrics: ["portfolioCount"],
    },
    goal: {
        labelKey: "reports.builder.entity.goal",
        rowsKey: "goals",
        defaultXAxis: "owner",
        defaultMetric: "goalCount",
        defaultDateField: "createdAt",
        sources: ["workspace", "teamGoals", "goalOwner", "specificGoal", "goalCycle"],
        dateFields: ["createdAt", "updatedAt", "startDate", "dueDate"],
        dimensions: ["owner", "goalStatus", "goalCycle", "team", "date"],
        filterFields: ["owner", "goalStatus", "goalCycle", "team", "date"],
        metrics: ["goalCount", "completedGoals"],
    },
};

const sourceConfigs = {
    workspace: {
        labelKey: "reports.builder.source.workspace",
        needsItem: false,
    },
    teamGoals: {
        labelKey: "reports.builder.source.teamGoals",
        needsItem: false,
    },
    myWork: {
        labelKey: "reports.builder.source.myWork",
        needsItem: false,
    },
    projectOwner: {
        labelKey: "reports.builder.source.projectOwner",
        needsItem: true,
        itemKind: "members",
    },
    goalOwner: {
        labelKey: "reports.builder.source.goalOwner",
        needsItem: true,
        itemKind: "members",
    },
    portfolioOwner: {
        labelKey: "reports.builder.source.portfolioOwner",
        needsItem: true,
        itemKind: "members",
    },
    portfolio: {
        labelKey: "reports.builder.source.portfolio",
        needsItem: true,
        itemKind: "portfolios",
    },
    specificPortfolio: {
        labelKey: "reports.builder.source.specificPortfolio",
        needsItem: true,
        itemKind: "portfolios",
    },
    specificProject: {
        labelKey: "reports.builder.source.specificProject",
        needsItem: true,
        itemKind: "projects",
    },
    specificGoal: {
        labelKey: "reports.builder.source.specificGoal",
        needsItem: true,
        itemKind: "goals",
    },
    goalCycle: {
        labelKey: "reports.builder.source.goalCycle",
        needsItem: true,
        itemKind: "cycles",
    },
};

const chartStyles = [
    {value: "bar", labelKey: "reports.builder.chart.bar", icon: "pi pi-chart-bar"},
    {value: "stackedBar", labelKey: "reports.builder.chart.stackedBar", icon: "pi pi-align-end"},
    {value: "groupedBar", labelKey: "reports.builder.chart.groupedBar", icon: "pi pi-table"},
    {value: "line", labelKey: "reports.builder.chart.line", icon: "pi pi-chart-line"},
    {value: "donut", labelKey: "reports.builder.chart.donut", icon: "pi pi-chart-pie"},
    {value: "number", labelKey: "reports.builder.chart.number", icon: "pi pi-hashtag"},
    {value: "lollipop", labelKey: "reports.builder.chart.lollipop", icon: "pi pi-minus"},
];

const dimensionConfigs = {
    assignee: {labelKey: "reports.builder.dimension.assignee", kind: "relation"},
    owner: {labelKey: "reports.builder.dimension.owner", kind: "relation"},
    project: {labelKey: "reports.builder.dimension.project", kind: "relation"},
    portfolio: {labelKey: "reports.builder.dimension.portfolio", kind: "relation"},
    goal: {labelKey: "reports.builder.dimension.goal", kind: "relation"},
    team: {labelKey: "reports.builder.dimension.team", kind: "relation"},
    taskStatus: {labelKey: "reports.builder.dimension.taskStatus", kind: "value"},
    projectStatus: {labelKey: "reports.builder.dimension.projectStatus", kind: "value"},
    portfolioStatus: {labelKey: "reports.builder.dimension.portfolioStatus", kind: "value"},
    goalStatus: {labelKey: "reports.builder.dimension.goalStatus", kind: "value"},
    dueStatus: {labelKey: "reports.builder.dimension.dueStatus", kind: "value"},
    taskType: {labelKey: "reports.builder.dimension.taskType", kind: "relation"},
    goalCycle: {labelKey: "reports.builder.dimension.goalCycle", kind: "value"},
    date: {labelKey: "reports.builder.dimension.date", kind: "date"},
};

const metricConfigs = {
    taskCount: {labelKey: "reports.builder.metric.taskCount", type: "count"},
    completedTasks: {labelKey: "reports.builder.metric.completedTasks", type: "predicateCount"},
    incompleteTasks: {labelKey: "reports.builder.metric.incompleteTasks", type: "predicateCount"},
    timeToComplete: {labelKey: "reports.builder.metric.timeToComplete", type: "number"},
    projectCount: {labelKey: "reports.builder.metric.projectCount", type: "count"},
    portfolioCount: {labelKey: "reports.builder.metric.portfolioCount", type: "count"},
    goalCount: {labelKey: "reports.builder.metric.goalCount", type: "count"},
    completedGoals: {labelKey: "reports.builder.metric.completedGoals", type: "predicateCount"},
};

const aggregations = [
    {value: "count", labelKey: "reports.builder.aggregation.count"},
    {value: "sum", labelKey: "reports.builder.aggregation.sum"},
    {value: "avg", labelKey: "reports.builder.aggregation.avg"},
    {value: "min", labelKey: "reports.builder.aggregation.min"},
    {value: "max", labelKey: "reports.builder.aggregation.max"},
];

const timeUnits = [
    {value: "yearly", labelKey: "reports.builder.timeUnit.yearly"},
    {value: "monthly", labelKey: "reports.builder.timeUnit.monthly"},
    {value: "weekly", labelKey: "reports.builder.timeUnit.weekly"},
    {value: "daily", labelKey: "reports.builder.timeUnit.daily"},
    {value: "hourly", labelKey: "reports.builder.timeUnit.hourly"},
    {value: "minute", labelKey: "reports.builder.timeUnit.minute"},
];

const filterOperators = [
    {value: "include", labelKey: "reports.builder.filter.include"},
    {value: "exclude", labelKey: "reports.builder.filter.exclude"},
];

const compactControlPt = {
    root: {className: "text-xs"},
    input: {className: "text-xs py-2"},
    label: {className: "text-xs"},
    item: {className: "text-xs"},
    token: {className: "text-xs"},
    emptyMessage: {className: "text-xs p-2"},
};

const compactDialogPt = {
    header: {className: "py-3 px-4"},
    title: {className: "text-base font-bold"},
    content: {className: "p-3 md:p-4 overflow-auto"},
    footer: {className: "p-3"},
};

function toArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
}

function valueId(value) {
    if (!value && value !== 0) return "";
    if (typeof value === "object") {
        if (value.member) return valueId(value.member);
        return `${value.documentId ?? value.id ?? value.value ?? ""}`;
    }
    return `${value}`;
}

function compactValues(values) {
    return toArray(values).filter(Boolean);
}

function uniqById(items) {
    const map = new Map();
    compactValues(items).forEach((item) => {
        const id = valueId(item);
        if (id && !map.has(id)) map.set(id, item);
    });
    return [...map.values()];
}

function sameId(left, right) {
    const leftId = valueId(left);
    const rightId = valueId(right);
    return !!leftId && !!rightId && leftId === rightId;
}

function labelFromObject(value, translate) {
    if (!value && value !== 0) return translate("reports.builder.unknown");
    if (typeof value !== "object") return `${value}`;
    const member = value.member || value.user || value.owner || value.assignee;
    if (member && member !== value) return labelFromObject(member, translate);
    return (
        value.fullName ||
        value.fullname ||
        value.displayName ||
        value.name ||
        value.title ||
        value.username ||
        value.mobile ||
        value.email ||
        value.label ||
        translate("reports.builder.unknown")
    );
}

function optionFromObject(value, translate) {
    const id = valueId(value);
    return {
        value: id || EMPTY_VALUE,
        label: labelFromObject(value, translate),
    };
}

function dateValue(row, field) {
    return row?.[field] || row?.attributes?.[field] || null;
}

function isDoneStatus(value) {
    return ["done", "achieved", "completed", "complete"].includes(`${value || ""}`.toLowerCase());
}

function taskDone(task) {
    return isDoneStatus(task?.taskStatus) || !!task?.doneAt || !!task?.completedAt;
}

function goalDone(goal) {
    return isDoneStatus(goal?.goalStatus);
}

function numericValue(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function daysBetween(start, end) {
    if (!start || !end) return null;
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return null;
    return Math.max(0, Math.round((endTime - startTime) / 86400000));
}

function getStatusLabel(value, translate) {
    const key = `reports.builder.status.${value}`;
    const translated = translate(key);
    return translated === key ? labelFromObject(value, translate) : translated;
}

function getDueStatus(task, translate) {
    if (taskDone(task)) return translate("reports.builder.due.done");
    if (!task?.dueDate) return translate("reports.builder.due.noDue");
    const due = new Date(task.dueDate);
    if (Number.isNaN(due.getTime())) return translate("reports.builder.due.noDue");
    const now = new Date();
    const sameDay = due.toDateString() === now.toDateString();
    if (sameDay) return translate("reports.builder.due.today");
    if (due.getTime() < now.getTime()) return translate("reports.builder.due.overdue");
    return translate("reports.builder.due.future");
}

function bucketDate(dateInput, granularity, translate) {
    if (!dateInput) return translate("reports.builder.unknown");
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return translate("reports.builder.unknown");
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");

    if (granularity === "yearly") return `${year}`;
    if (granularity === "monthly") return `${year}/${month}`;
    if (granularity === "weekly") {
        const firstDay = new Date(date.getFullYear(), 0, 1);
        const week = Math.ceil((((date - firstDay) / 86400000) + firstDay.getDay() + 1) / 7);
        return `${year} - ${translate("reports.builder.timeUnit.weekly")} ${week}`;
    }
    if (granularity === "hourly") return `${year}/${month}/${day} ${hours}:00`;
    if (granularity === "minute") return `${year}/${month}/${day} ${hours}:${minutes}`;
    return `${year}/${month}/${day}`;
}

function projectsForTask(task, context) {
    const taskProjects = toArray(task?.projects);
    if (taskProjects.length) return taskProjects;
    return context.projects.filter((project) => toArray(project?.tasks).some((taskItem) => sameId(taskItem, task)));
}

function ownerOfRow(row, entity) {
    if (entity === "task") return row?.assignee || row?.owner || row?.creatorUser;
    return row?.owner || row?.assignee || row?.creatorUser || row?.owner;
}

function projectOwnerMatches(row, entity, context, ids) {
    return rowProjects(row, entity, context).some((project) => ids.some((id) => sameId(project?.owner || project?.owner, id)));
}

function isCurrentUserRelated(row, entity, currentUser, context) {
    if (!currentUser) return false;
    if (sameId(ownerOfRow(row, entity), currentUser)) return true;
    if (entity === "task") {
        if (sameId(row?.creatorUser, currentUser)) return true;
        return toArray(row?.members).some((membership) => sameId(membership, currentUser));
    }
    if (entity === "project") {
        return toArray(row?.members).some((membership) => sameId(membership, currentUser));
    }
    if (entity === "portfolio") {
        return toArray(row?.projects).some((project) => isCurrentUserRelated(project, "project", currentUser, context));
    }
    return false;
}

function rowPortfolios(row, entity, context) {
    if (entity === "portfolio") return [row];
    if (entity === "project") return toArray(row?.portfolios);
    if (entity === "task") {
        return projectsForTask(row, context).flatMap((project) => toArray(project?.portfolios));
    }
    return [];
}

function rowGoals(row, entity) {
    if (entity === "goal") return [row];
    if (entity === "project") return toArray(row?.goals);
    return [];
}

function rowProjects(row, entity, context) {
    if (entity === "project") return [row];
    if (entity === "task") return projectsForTask(row, context);
    if (entity === "portfolio") return toArray(row?.projects);
    if (entity === "goal") return toArray(row?.projects);
    return [];
}

function relationLabels(values, translate) {
    const labels = compactValues(values).map((item) => labelFromObject(item, translate)).filter(Boolean);
    return labels.length ? labels : [translate("reports.builder.unknown")];
}

function dimensionLabels(row, entity, dimension, config, context, translate) {
    if (dimension === "date") {
        return [bucketDate(dateValue(row, config.dateField), config.timeUnit, translate)];
    }
    if (dimension === "assignee") return relationLabels([row?.assignee], translate);
    if (dimension === "owner") return relationLabels([ownerOfRow(row, entity)], translate);
    if (dimension === "project") return relationLabels(rowProjects(row, entity, context), translate);
    if (dimension === "portfolio") return relationLabels(rowPortfolios(row, entity, context), translate);
    if (dimension === "goal") return relationLabels(rowGoals(row, entity), translate);
    if (dimension === "team") return relationLabels([row?.team], translate);
    if (dimension === "taskType") return relationLabels([row?.task_type || row?.taskType], translate);
    if (dimension === "dueStatus") return [getDueStatus(row, translate)];
    if (dimension === "taskStatus") return [getStatusLabel(row?.taskStatus || row?.status || "unknown", translate)];
    if (dimension === "projectStatus") return [getStatusLabel(row?.projectStatus || row?.status || "unknown", translate)];
    if (dimension === "portfolioStatus") return [getStatusLabel(row?.portfolioStatus || row?.status || "unknown", translate)];
    if (dimension === "goalStatus") return [getStatusLabel(row?.goalStatus || row?.status || "unknown", translate)];
    if (dimension === "goalCycle") return [row?.cycle || row?.period || row?.season || translate("reports.builder.unknown")];
    return [translate("reports.builder.unknown")];
}

function metricValues(row, metric, entity) {
    if (metric === "taskCount" || metric === "projectCount" || metric === "portfolioCount" || metric === "goalCount") return 1;
    if (metric === "completedTasks") return taskDone(row) ? 1 : 0;
    if (metric === "incompleteTasks") return taskDone(row) ? 0 : 1;
    if (metric === "completedGoals") return goalDone(row) ? 1 : 0;
    if (metric === "timeToComplete") return daysBetween(row?.createdAt, row?.doneAt || row?.completedAt);
    if (metric === "loggedTime") {
        return numericValue(row?.loggedTime ?? row?.timeSpent ?? row?.spentTime ?? row?.duration ?? row?.workTime);
    }
    if (metric === "projectTasks") return toArray(row?.tasks).length;
    if (metric === "projectProgress" || metric === "goalProgress") {
        return numericValue(row?.progress ?? row?.percentage ?? row?.percent);
    }
    if (metric === "projectGoals") return toArray(row?.goals).length;
    if (metric === "portfolioProjects") return toArray(row?.projects).length;
    if (metric === "goalProjects") return toArray(row?.projects).length;
    if (entity === "portfolio") return toArray(row?.projects).length;
    return null;
}

function aggregate(values, aggregation) {
    const numbers = values.map(numericValue).filter((value) => value !== null);
    if (aggregation === "count") return numbers.length;
    if (!numbers.length) return 0;
    if (aggregation === "avg") return Math.round((numbers.reduce((sum, value) => sum + value, 0) / numbers.length) * 10) / 10;
    if (aggregation === "min") return Math.min(...numbers);
    if (aggregation === "max") return Math.max(...numbers);
    return Math.round(numbers.reduce((sum, value) => sum + value, 0) * 10) / 10;
}

function metricTotal(rows, metric, aggregation, entity) {
    const metricConfig = metricConfigs[metric];
    if (!metricConfig) return 0;
    if (metricConfig.type === "count") return rows.length;
    if (metricConfig.type === "predicateCount") {
        return rows.reduce((sum, row) => sum + Number(metricValues(row, metric, entity) || 0), 0);
    }
    return aggregate(rows.map((row) => metricValues(row, metric, entity)), aggregation);
}

function itemIds(values) {
    return toArray(values).map(valueId).filter(Boolean);
}

function selectedIds(config) {
    return itemIds(config.sourceItemIds);
}

function applySource(rows, config, context) {
    const entity = config.entity;
    const ids = selectedIds(config);
    if (config.source === "myWork") {
        return rows.filter((row) => isCurrentUserRelated(row, entity, context.currentUser, context));
    }
    if (config.source === "projectOwner" || config.source === "portfolioOwner" || config.source === "goalOwner") {
        if (entity === "task" || entity === "project" || entity === "portfolio") {
            return rows.filter((row) => projectOwnerMatches(row, entity, context, ids) || ids.some((id) => sameId(ownerOfRow(row, entity), id)));
        }
        return rows.filter((row) => ids.some((id) => sameId(ownerOfRow(row, entity), id)));
    }
    if (config.source === "teamGoals") {
        return rows.filter((row) => !!row?.team);
    }
    if (config.source === "specificProject") {
        return rows.filter((row) => rowProjects(row, entity, context).some((project) => ids.some((id) => sameId(project, id))));
    }
    if (config.source === "portfolio" || config.source === "specificPortfolio") {
        return rows.filter((row) => rowPortfolios(row, entity, context).some((portfolio) => ids.some((id) => sameId(portfolio, id))));
    }
    if (config.source === "specificGoal") {
        return rows.filter((row) => rowGoals(row, entity).some((goal) => ids.some((id) => sameId(goal, id))));
    }
    if (config.source === "goalCycle") {
        return rows.filter((row) => ids.includes(`${row?.cycle || row?.period || row?.season || EMPTY_VALUE}`));
    }
    return rows;
}

function dateInsideRange(value, range) {
    if (!range?.[0] || !range?.[1] || !value) return true;
    const date = new Date(value).getTime();
    const start = new Date(range[0]).setHours(0, 0, 0, 0);
    const end = new Date(range[1]).setHours(23, 59, 59, 999);
    return Number.isFinite(date) && date >= start && date <= end;
}

function applyFilters(rows, config, context, translate) {
    return rows.filter((row) => {
        return config.filters.every((filter) => {
            const dimension = dimensionConfigs[filter.field];
            if (!dimension) return true;
            if (dimension.kind === "date") {
                const inRange = dateInsideRange(dateValue(row, config.dateField), filter.range);
                return filter.operator === "exclude" ? !inRange : inRange;
            }
            const labels = dimensionLabels(row, config.entity, filter.field, config, context, translate);
            const activeValues = toArray(filter.values);
            if (!activeValues.length) return true;
            const matched = labels.some((label) => activeValues.includes(label));
            return filter.operator === "exclude" ? !matched : matched;
        });
    });
}

function buildSourceOptions(entity, translate) {
    return entityConfigs[entity].sources.map((source) => ({
        value: source,
        label: translate(sourceConfigs[source].labelKey),
    }));
}

function cycleOptions(goals, translate) {
    const cycles = uniqById(goals.map((goal) => goal?.cycle || goal?.period || goal?.season).filter(Boolean));
    return cycles.map((cycle) => ({value: `${cycle}`, label: `${cycle}`}));
}

function sourceItemOptions(source, context, translate) {
    const itemKind = sourceConfigs[source]?.itemKind;
    if (itemKind === "members") return context.members.map((member) => optionFromObject(member, translate));
    if (itemKind === "projects") return context.projects.map((project) => optionFromObject(project, translate));
    if (itemKind === "portfolios") return context.portfolios.map((portfolio) => optionFromObject(portfolio, translate));
    if (itemKind === "goals") return context.goals.map((goal) => optionFromObject(goal, translate));
    if (itemKind === "cycles") return cycleOptions(context.goals, translate);
    return [];
}

function filterFieldsForConfig(config) {
    const fields = entityConfigs[config.entity]?.filterFields || [];
    const sourceFieldMap = {
        projectOwner: "owner",
        portfolioOwner: "owner",
        goalOwner: "owner",
        portfolio: "portfolio",
        specificPortfolio: "portfolio",
        specificProject: "project",
        specificGoal: "goal",
        goalCycle: "goalCycle",
    };
    const sourceField = sourceFieldMap[config.source];
    return sourceField ? fields.filter((field) => field !== sourceField) : fields;
}

function chartStyleNeedsAxis(style) {
    return ["bar", "stackedBar", "groupedBar", "line", "lollipop"].includes(style);
}

function chartStyleAllowsGroup(style) {
    return ["stackedBar", "groupedBar"].includes(style);
}

function chartStyleAllowsMultiMetrics(style) {
    return ["line", "lollipop"].includes(style);
}

function chartStyleRequiresGroup(style) {
    return ["stackedBar", "groupedBar", "donut"].includes(style);
}

function metricNeedsAggregation(metric) {
    return metricConfigs[metric]?.type === "number";
}

function chartStyleType(style) {
    if (style === "line") return "line";
    if (style === "donut") return "doughnut";
    return "bar";
}

function createDataset(label, data, colorIndex, style, grouped) {
    const color = chartColors[colorIndex % chartColors.length];
    return {
        label,
        data,
        backgroundColor: style === "line" ? `${color}22` : color,
        borderColor: color,
        borderWidth: style === "line" ? 3 : 0,
        borderRadius: style === "lollipop" ? 999 : 2,
        barThickness: style === "lollipop" ? 6 : undefined,
        tension: style === "line" ? 0.35 : undefined,
        fill: style === "line" ? false : undefined,
        stack: style === "stackedBar" ? "stack-1" : grouped ? undefined : "stack-1",
    };
}

function withBenchmarkDataset(data, config, translate) {
    if (!config.showBenchmark || config.benchmarkValue === null || config.benchmarkValue === undefined || config.chartStyle === "donut") return data;
    return {
        ...data,
        datasets: [
            ...data.datasets,
            {
                type: "line",
                label: translate("reports.builder.benchmark.label"),
                data: data.labels.map(() => config.benchmarkValue),
                borderColor: "#7f1d0a",
                borderDash: [6, 4],
                borderWidth: 2,
                pointRadius: 0,
                fill: false,
                tension: 0,
            },
        ],
    };
}

function rowsByLabels(rows, getLabels) {
    const groups = new Map();
    rows.forEach((row) => {
        getLabels(row).forEach((label) => {
            const safeLabel = label || "";
            if (!groups.has(safeLabel)) groups.set(safeLabel, []);
            groups.get(safeLabel).push(row);
        });
    });
    return groups;
}

function buildChartPreview(config, filteredRows, context, translate) {
    const metrics = config.metrics.length ? config.metrics : [entityConfigs[config.entity].defaultMetric];
    const aggregation = config.aggregation || "sum";
    const title = config.title?.trim() || translate("reports.builder.defaultTitle");

    if (config.chartStyle === "number") {
        const metric = metrics[0];
        return {
            title,
            kind: "number",
            value: metricTotal(filteredRows, metric, aggregation, config.entity),
            label: translate(metricConfigs[metric]?.labelKey),
            rowsCount: filteredRows.length,
        };
    }

    const groupDimension = config.chartStyle === "donut" ? config.groupBy : config.xAxis;
    const groups = rowsByLabels(filteredRows, (row) => dimensionLabels(row, config.entity, groupDimension, config, context, translate));
    const labels = [...groups.keys()].slice(0, 18);

    if (config.chartStyle === "donut") {
        const metric = metrics[0];
        return {
            title,
            kind: "chart",
            type: "doughnut",
            data: {
                labels,
                datasets: [{
                    label: translate(metricConfigs[metric]?.labelKey),
                    data: labels.map((label) => metricTotal(groups.get(label) || [], metric, aggregation, config.entity)),
                    backgroundColor: labels.map((_, index) => chartColors[index % chartColors.length]),
                    borderWidth: 0,
                }],
            },
            rowsCount: filteredRows.length,
        };
    }

    if (config.groupBy && chartStyleAllowsGroup(config.chartStyle)) {
        const xGroups = rowsByLabels(filteredRows, (row) => dimensionLabels(row, config.entity, config.xAxis, config, context, translate));
        const xLabels = [...xGroups.keys()].slice(0, 18);
        const groupLabels = [...rowsByLabels(filteredRows, (row) => dimensionLabels(row, config.entity, config.groupBy, config, context, translate)).keys()].slice(0, 8);
        const metric = metrics[0];

        const data = {
            labels: xLabels,
            datasets: groupLabels.map((groupLabel, index) => createDataset(groupLabel, xLabels.map((xLabel) => {
                const rows = (xGroups.get(xLabel) || []).filter((row) => dimensionLabels(row, config.entity, config.groupBy, config, context, translate).includes(groupLabel));
                return metricTotal(rows, metric, aggregation, config.entity);
            }), index, config.chartStyle, true)),
        };

        return {
            title,
            kind: "chart",
            type: chartStyleType(config.chartStyle),
            data: withBenchmarkDataset(data, config, translate),
            rowsCount: filteredRows.length,
        };
    }

    const data = {
        labels,
        datasets: metrics.map((metric, index) => createDataset(translate(metricConfigs[metric]?.labelKey), labels.map((label) => {
            return metricTotal(groups.get(label) || [], metric, aggregation, config.entity);
        }), index, config.chartStyle, metrics.length > 1)),
    };

    return {
        title,
        kind: "chart",
        type: chartStyleType(config.chartStyle),
        data: withBenchmarkDataset(data, config, translate),
        rowsCount: filteredRows.length,
    };
}

// Chart text is drawn on a canvas: it cannot inherit the page font ("inherit" is not a font), so the app font is named
// here. Without it the legend, axis labels and tooltips fell back to a tiny default font.
export const CHART_FONT_FAMILY = "Shabnam-FD, IranYekan, Tahoma, sans-serif";
const chartFont = (size = 12, weight = "normal") => ({family: CHART_FONT_FAMILY, size, weight});

export function chartOptions(config, translate) {
    const stacked = config.chartStyle === "stackedBar";
    const horizontal = config.chartStyle === "lollipop";

    return {
        maintainAspectRatio: false,
        responsive: true,
        indexAxis: horizontal ? "y" : "x",
        plugins: {
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    padding: 14,
                    color: "#4d2a20",
                    font: chartFont(12),
                },
            },
            tooltip: {
                rtl: true,
                titleAlign: "right",
                bodyAlign: "right",
                titleFont: chartFont(12, "bold"),
                bodyFont: chartFont(12),
                padding: 10,
                callbacks: {
                    title: (items) => items?.[0]?.label || "",
                    label: (item) => `${item.dataset.label}: ${item.formattedValue}`,
                },
            },
        },
        scales: config.chartStyle === "donut" ? undefined : {
            x: {
                stacked,
                grid: {color: "#f2e6e2"},
                ticks: {color: "#7b625b", font: chartFont(12)},
                title: {
                    display: !!config.xAxis,
                    text: translate(dimensionConfigs[config.xAxis]?.labelKey || "reports.builder.axis"),
                    color: "#8b6b62",
                    font: chartFont(12, "bold"),
                },
            },
            y: {
                stacked,
                beginAtZero: true,
                grid: {color: "#f2e6e2"},
                ticks: {color: "#7b625b", precision: 0, font: chartFont(12)},
            },
        },
    };
}

function validateConfig(config, context, translate) {
    const errors = [];
    const entity = entityConfigs[config.entity];
    if (!entity) errors.push(translate("reports.builder.validation.entityRequired"));
    if (!config.source) errors.push(translate("reports.builder.validation.sourceRequired"));
    if (sourceConfigs[config.source]?.needsItem && !selectedIds(config).length) {
        errors.push(translate("reports.builder.validation.sourceItemRequired"));
    }
    if (!config.chartStyle) errors.push(translate("reports.builder.validation.chartRequired"));
    if (!config.metrics.length) errors.push(translate("reports.builder.validation.metricRequired"));
    if (chartStyleNeedsAxis(config.chartStyle) && !config.xAxis) {
        errors.push(translate("reports.builder.validation.axisRequired"));
    }
    if (chartStyleRequiresGroup(config.chartStyle) && !config.groupBy) {
        errors.push(translate("reports.builder.validation.groupRequired"));
    }
    if (config.xAxis && config.groupBy && config.xAxis === config.groupBy) {
        errors.push(translate("reports.builder.validation.sameAxisGroup"));
    }
    if (config.showBenchmark && (config.benchmarkValue === null || config.benchmarkValue === undefined)) {
        errors.push(translate("reports.builder.validation.benchmarkRequired"));
    }
    const filterFields = config.filters.map((filter) => filter.field).filter(Boolean);
    if (new Set(filterFields).size !== filterFields.length) {
        errors.push(translate("reports.builder.validation.duplicateFilter"));
    }
    if (!toArray(context[entity?.rowsKey]).length) {
        errors.push(translate("reports.builder.validation.noEntityData"));
    }
    return errors;
}

function createDefaultConfig() {
    return {
        title: "",
        entity: "task",
        source: "workspace",
        sourceItemIds: [],
        chartStyle: "bar",
        xAxis: "project",
        groupBy: "",
        metrics: ["taskCount"],
        aggregation: "sum",
        timeUnit: "daily",
        dateField: "createdAt",
        showDataLabels: false,
        showBenchmark: false,
        benchmarkValue: null,
        filters: [],
    };
}

function normalizeConfig(nextConfig) {
    const entityConfig = entityConfigs[nextConfig.entity];
    if (!entityConfig) return nextConfig;

    let xAxis = nextConfig.xAxis;
    if (xAxis && !entityConfig.dimensions.includes(xAxis)) xAxis = entityConfig.defaultXAxis;

    const validMetrics = nextConfig.metrics.filter((metric) => entityConfig.metrics.includes(metric));
    const metrics = validMetrics.length ? validMetrics : [entityConfig.defaultMetric];
    const chartStyle = nextConfig.chartStyle || "bar";
    const normalizedMetrics = chartStyleAllowsMultiMetrics(chartStyle) ? metrics : [metrics[0]];
    const aggregation = normalizedMetrics.some(metricNeedsAggregation) && !["sum", "avg"].includes(nextConfig.aggregation) ? "sum" : nextConfig.aggregation;
    const validFilterFields = filterFieldsForConfig(nextConfig);
    const groupBy = (() => {
        if (!nextConfig.groupBy || !entityConfig.dimensions.includes(nextConfig.groupBy)) {
            return chartStyleRequiresGroup(chartStyle) ? entityConfig.dimensions.find((dimension) => dimension !== xAxis) || "" : "";
        }
        if (!chartStyleRequiresGroup(chartStyle)) return "";
        return nextConfig.groupBy;
    })();

    return {
        ...nextConfig,
        source: entityConfig.sources.includes(nextConfig.source) ? nextConfig.source : entityConfig.sources[0],
        sourceItemIds: entityConfig.sources.includes(nextConfig.source) ? nextConfig.sourceItemIds : [],
        xAxis: chartStyleNeedsAxis(chartStyle) ? xAxis : "",
        groupBy,
        metrics: normalizedMetrics,
        aggregation,
        dateField: entityConfig.dateFields.includes(nextConfig.dateField) ? nextConfig.dateField : entityConfig.defaultDateField,
        filters: nextConfig.filters.filter((filter) => validFilterFields.includes(filter.field)),
        showBenchmark: ["donut", "number"].includes(chartStyle) ? false : nextConfig.showBenchmark,
    };
}

function normalizeReportContext(dataContext) {
    return {
        tasks: toArray(dataContext?.tasks),
        projects: toArray(dataContext?.projects),
        portfolios: toArray(dataContext?.portfolios),
        goals: toArray(dataContext?.goals),
        members: toArray(dataContext?.members),
        currentUser: dataContext?.currentUser,
        loading: !!dataContext?.loading,
        error: dataContext?.error,
    };
}

// a saved report's config (any older shape) -> the config the builder works with
export function configFromSaved(configInput) {
    return normalizeConfig({
        ...createDefaultConfig(),
        ...configInput,
        metrics: toArray(configInput?.metrics?.length ? configInput.metrics : configInput?.metric),
        sourceItemIds: toArray(configInput?.sourceItemIds?.length ? configInput.sourceItemIds : configInput?.sourceItems),
        xAxis: configInput?.xAxis || configInput?.dimension,
        dimension: configInput?.dimension || configInput?.xAxis,
        filters: toArray(configInput?.filters),
        showDataLabels: configInput?.annotations?.showDataLabels ?? configInput?.showDataLabels ?? false,
        showBenchmark: configInput?.annotations?.showBenchmark ?? configInput?.showBenchmark ?? false,
        benchmarkValue: configInput?.annotations?.benchmarkValue ?? configInput?.benchmarkValue ?? null,
    });
}

export function buildReportPreview(configInput, dataContext, translate) {
    const context = normalizeReportContext(dataContext);
    const config = configFromSaved(configInput);
    const entityConfig = entityConfigs[config.entity];
    const validationErrors = validateConfig(config, context, translate);
    if (!entityConfig || validationErrors.length) return {config, preview: null, validationErrors};

    const rawRows = toArray(context[entityConfig.rowsKey]);
    const sourceRows = applySource(rawRows, config, context);
    const filteredRows = applyFilters(sourceRows, config, context, translate);
    const preview = filteredRows.length ? buildChartPreview(config, filteredRows, context, translate) : {kind: "empty", rowsCount: 0};

    return {config, preview, validationErrors};
}

function controlOptionTemplate(option) {
    if (!option) return null;
    return (
        <div className="flex align-items-center gap-2 text-xs">
            {option.icon && <span className={`${option.icon} text-primary`}/>}
            <span>{option.label}</span>
        </div>
    );
}

// `report` (a saved report) switches the dialog to editing it: the same form, filled with the report's settings
function DynamicReportBuilderDialog({visible, onHide, dataContext, onSubmit, saving = false, report = null}) {
    const translate = useTranslate();
    const [config, setConfig] = useState(createDefaultConfig);
    const [resetNoticeKey, setResetNoticeKey] = useState("");

    const context = useMemo(() => normalizeReportContext(dataContext), [dataContext]);

    const editing = Boolean(report);

    useEffect(() => {
        if (visible) {
            setConfig(report?.config ? configFromSaved({...report.config, title: report.config.title || report.title}) : createDefaultConfig());
            setResetNoticeKey("");
        }
        // opens with the report it was opened for; later changes of the report object must not reset the form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, report?.id]);

    const entityConfig = entityConfigs[config.entity];

    const optionSets = useMemo(() => {
        const dimensions = entityConfig.dimensions.map((dimension) => ({
            value: dimension,
            label: translate(dimensionConfigs[dimension].labelKey),
        }));
        const metrics = entityConfig.metrics.map((metric) => ({
            value: metric,
            label: translate(metricConfigs[metric].labelKey),
        }));
        const filterFields = filterFieldsForConfig(config);
        const filters = filterFields.map((field) => ({
            value: field,
            label: translate(dimensionConfigs[field].labelKey),
        }));
        return {
            entities: Object.entries(entityConfigs).map(([value, item]) => ({value, label: translate(item.labelKey)})),
            sources: buildSourceOptions(config.entity, translate),
            sourceItems: sourceItemOptions(config.source, context, translate),
            chartStyles: chartStyles.map((style) => ({
                value: style.value,
                label: translate(style.labelKey),
                icon: style.icon,
            })),
            dimensions,
            groupDimensions: dimensions,
            filters,
            metrics,
            dateFields: entityConfig.dateFields.map((field) => ({
                value: field,
                label: translate(`reports.builder.dateField.${field}`),
            })),
            aggregations: aggregations
                .filter((item) => !config.metrics.some(metricNeedsAggregation) || ["sum", "avg"].includes(item.value))
                .map((item) => ({value: item.value, label: translate(item.labelKey)})),
            timeUnits: timeUnits.map((item) => ({value: item.value, label: translate(item.labelKey)})),
            filterOperators: filterOperators.map((item) => ({value: item.value, label: translate(item.labelKey)})),
        };
    }, [config, context, entityConfig, translate]);

    const rawRows = toArray(context[entityConfig.rowsKey]);
    const validationErrors = validateConfig(config, context, translate);

    const preview = useMemo(() => {
        if (validationErrors.length) return null;
        const sourceRows = applySource(rawRows, config, context);
        const filteredRows = applyFilters(sourceRows, config, context, translate);
        if (!filteredRows.length) return {kind: "empty", rowsCount: 0};
        return buildChartPreview(config, filteredRows, context, translate);
    }, [config, context, rawRows, translate, validationErrors.length]);

    const updateConfig = (patch, noticeKey = "") => {
        setConfig((current) => normalizeConfig({...current, ...patch}));
        if (noticeKey) setResetNoticeKey(noticeKey);
    };

    const addFilter = () => {
        const usedFields = config.filters.map((filter) => filter.field);
        const defaultField = filterFieldsForConfig(config).find((field) => !usedFields.includes(field));
        if (!defaultField) {
            setResetNoticeKey("reports.builder.validation.noMoreFilters");
            return;
        }
        updateConfig({
            filters: [
                ...config.filters,
                {id: Date.now(), field: defaultField, operator: "include", values: [], range: null},
            ],
        });
    };

    const updateFilter = (filterId, patch) => {
        updateConfig({
            filters: config.filters.map((filter) => filter.id === filterId ? {...filter, ...patch} : filter),
        });
    };

    const removeFilter = (filterId) => {
        updateConfig({filters: config.filters.filter((filter) => filter.id !== filterId)});
    };

    const filterValueOptions = (field) => {
        const rows = applySource(rawRows, config, context);
        const values = new Set();
        rows.forEach((row) => {
            dimensionLabels(row, config.entity, field, config, context, translate).forEach((label) => values.add(label));
        });
        return [...values].map((value) => ({value, label: value}));
    };

    const filterFieldOptions = (activeField) => {
        const usedFields = config.filters.map((filter) => filter.field).filter((field) => field !== activeField);
        return optionSets.filters.filter((option) => option.value === activeField || !usedFields.includes(option.value));
    };

    const showAggregation = config.metrics.some(metricNeedsAggregation);

    const handleCreate = () => {
        const sourceRows = applySource(rawRows, config, context);
        const filteredRows = applyFilters(sourceRows, config, context, translate);
        const finalPreview = buildChartPreview(config, filteredRows, context, translate);
        onSubmit?.({
            config: {...config, generatedAt: new Date().toISOString()},
            preview: finalPreview,
            rowsCount: filteredRows.length,
        });
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={translate(editing ? "reports.builder.editTitle" : "reports.builder.title")}
            modal
            dismissableMask
            footer={<div className="flex align-items-center justify-content-end gap-2">
                <Button
                    label={translate(editing ? "reports.builder.save" : "reports.builder.create")}
                    icon="pi pi-check"
                    onClick={handleCreate}
                    disabled={saving || context.loading || context.error || validationErrors.length > 0 || !preview || preview.kind === "empty"}
                    loading={saving}
                    size="small"
                    className="text-xs"
                />
                <Button
                    label={translate("cancel")}
                    onClick={onHide}
                    outlined
                    severity="secondary"
                    size="small"
                    className="text-xs"
                />
            </div>}
            className="w-11 lg:w-10 xl:w-9 rtl"
            contentClassName="overflow-auto"
            pt={compactDialogPt}>
            <div className="grid formgrid text-xs">
                <div className="col-12 lg:col-5 flex flex-column gap-3">
                    <div className="surface-card border-1 border-200 border-round p-3">
                        <h3 className="m-0 mb-3 text-sm font-bold text-900">{translate("reports.builder.section.details")}</h3>
                        <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                            <span>{translate("reports.builder.reportTitle.label")}</span>
                            <InputText
                                value={config.title}
                                onChange={(event) => updateConfig({title: event.target.value})}
                                placeholder={translate("reports.builder.reportTitle.placeholder")}
                                className="w-full text-xs"
                                pt={compactControlPt}
                            />
                        </label>
                        <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                            <span>{translate("reports.builder.entity.label")}</span>
                            <Dropdown
                                value={config.entity}
                                options={optionSets.entities}
                                onChange={(event) => updateConfig({
                                    entity: event.value,
                                    source: entityConfigs[event.value].sources[0],
                                    sourceItemIds: [],
                                    xAxis: entityConfigs[event.value].defaultXAxis,
                                    groupBy: "",
                                    metrics: [entityConfigs[event.value].defaultMetric],
                                    dateField: entityConfigs[event.value].defaultDateField,
                                    filters: [],
                                }, "reports.builder.resetNotice")}
                                className="w-full text-xs"
                                pt={compactControlPt}
                            />
                        </label>
                        <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                            <span>{translate("reports.builder.source.label")}</span>
                            <Dropdown
                                value={config.source}
                                options={optionSets.sources}
                                onChange={(event) => updateConfig({source: event.value, sourceItemIds: []}, "reports.builder.resetNotice")}
                                className="w-full text-xs"
                                pt={compactControlPt}
                            />
                        </label>
                        {sourceConfigs[config.source]?.needsItem && (
                            <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                                <span>{translate("reports.builder.sourceItem.label")}</span>
                                <MultiSelect
                                    value={config.sourceItemIds}
                                    options={optionSets.sourceItems}
                                    onChange={(event) => updateConfig({sourceItemIds: event.value})}
                                    placeholder={translate("reports.builder.sourceItem.placeholder")}
                                    display="chip"
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            </label>
                        )}
                        <label className="flex flex-column gap-1 text-xs text-600">
                            <span>{translate("reports.builder.chartStyle.label")}</span>
                            <Dropdown
                                value={config.chartStyle}
                                options={optionSets.chartStyles}
                                onChange={(event) => updateConfig({chartStyle: event.value}, "reports.builder.resetNotice")}
                                className="w-full text-xs"
                                optionLabel="label"
                                itemTemplate={controlOptionTemplate}
                                valueTemplate={controlOptionTemplate}
                                pt={compactControlPt}
                            />
                        </label>
                    </div>

                    <div className="surface-card border-1 border-200 border-round p-3">
                        <h3 className="m-0 mb-3 text-sm font-bold text-900">{translate("reports.builder.section.data")}</h3>
                        {resetNoticeKey && (
                            <Message severity="info" text={translate(resetNoticeKey)} className="w-full text-xs mb-3"/>
                        )}
                        {chartStyleNeedsAxis(config.chartStyle) && (
                            <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                                <span>{translate("reports.builder.xAxis.label")}</span>
                                <Dropdown
                                    value={config.xAxis}
                                    options={optionSets.dimensions}
                                    onChange={(event) => updateConfig({xAxis: event.value})}
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            </label>
                        )}
                        {chartStyleRequiresGroup(config.chartStyle) && (
                            <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                                <span>{translate(config.chartStyle === "donut" ? "reports.builder.groupBy.requiredLabel" : "reports.builder.groupBy.label")}</span>
                                <Dropdown
                                    value={config.groupBy}
                                    options={[{value: "", label: translate("reports.builder.none")}, ...optionSets.groupDimensions]}
                                    onChange={(event) => updateConfig({groupBy: event.value})}
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            </label>
                        )}
                        <label className="flex flex-column gap-1 mb-3 text-xs text-600">
                            <span>{translate("reports.builder.metric.label")}</span>
                            {chartStyleAllowsMultiMetrics(config.chartStyle) ? (
                                <MultiSelect
                                    value={config.metrics}
                                    options={optionSets.metrics}
                                    onChange={(event) => updateConfig({metrics: event.value})}
                                    display="chip"
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            ) : (
                                <Dropdown
                                    value={config.metrics[0]}
                                    options={optionSets.metrics}
                                    onChange={(event) => updateConfig({metrics: [event.value]})}
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            )}
                        </label>
                        {(config.xAxis === "date" || config.groupBy === "date") && (
                            <div className="grid formgrid">
                                <label className="col-12 md:col-6 flex flex-column gap-1 mb-3 text-xs text-600">
                                    <span>{translate("reports.builder.dateField.label")}</span>
                                    <Dropdown
                                        value={config.dateField}
                                        options={optionSets.dateFields}
                                        onChange={(event) => updateConfig({dateField: event.value})}
                                        className="w-full text-xs"
                                        pt={compactControlPt}
                                    />
                                </label>
                                <label className="col-12 md:col-6 flex flex-column gap-1 mb-3 text-xs text-600">
                                    <span>{translate("reports.builder.timeUnit.label")}</span>
                                    <Dropdown
                                        value={config.timeUnit}
                                        options={optionSets.timeUnits}
                                        onChange={(event) => updateConfig({timeUnit: event.value})}
                                        className="w-full text-xs"
                                        pt={compactControlPt}
                                    />
                                </label>
                            </div>
                        )}
                        {showAggregation && (
                            <label className="flex flex-column gap-1 text-xs text-600">
                                <span>{translate("reports.builder.aggregation.label")}</span>
                                <Dropdown
                                    value={config.aggregation}
                                    options={optionSets.aggregations}
                                    onChange={(event) => updateConfig({aggregation: event.value})}
                                    className="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            </label>
                        )}
                    </div>

                    <div className="surface-card border-1 border-200 border-round p-3">
                        <div className="flex align-items-center justify-content-between gap-2 mb-3">
                            <h3 className="m-0 text-sm font-bold text-900">{translate("reports.builder.section.filters")}</h3>
                            <Button
                                label={translate("reports.builder.filter.add")}
                                icon="pi pi-plus"
                                size="small"
                                text
                                className="text-xs"
                                onClick={addFilter}
                            />
                        </div>
                        {!config.filters.length && (
                            <p className="m-0 text-xs text-500">{translate("reports.builder.filter.empty")}</p>
                        )}
                        {config.filters.map((filter) => {
                            const dimension = dimensionConfigs[filter.field];
                            return (
                                <div className="surface-50 border-1 border-200 border-round p-2 mb-2 flex flex-column gap-2" key={filter.id}>
                                    <Dropdown
                                        value={filter.field}
                                        options={filterFieldOptions(filter.field)}
                                        onChange={(event) => updateFilter(filter.id, {field: event.value, values: [], range: null})}
                                        className="w-full text-xs"
                                        pt={compactControlPt}
                                    />
                                    <Dropdown
                                        value={filter.operator}
                                        options={optionSets.filterOperators}
                                        onChange={(event) => updateFilter(filter.id, {operator: event.value})}
                                        className="w-full text-xs"
                                        pt={compactControlPt}
                                    />
                                    {dimension?.kind === "date" ? (
                                        <Calendar
                                            value={filter.range}
                                            onChange={(event) => updateFilter(filter.id, {range: event.value})}
                                            selectionMode="range"
                                            readOnlyInput
                                            placeholder={translate("reports.builder.filter.dateRange")}
                                            className="w-full text-xs"
                                            inputClassName="text-xs"
                                            pt={compactControlPt}
                                        />
                                    ) : (
                                        <MultiSelect
                                            value={filter.values}
                                            options={filterValueOptions(filter.field)}
                                            onChange={(event) => updateFilter(filter.id, {values: event.value})}
                                            placeholder={translate("reports.builder.filter.values")}
                                            display="chip"
                                            className="w-full text-xs"
                                            pt={compactControlPt}
                                        />
                                    )}
                                    <Button
                                        icon="pi pi-trash"
                                        text
                                        rounded
                                        size="small"
                                        severity="danger"
                                        aria-label={translate("reports.builder.filter.remove")}
                                        onClick={() => removeFilter(filter.id)}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <div className="surface-card border-1 border-200 border-round p-3">
                        <h3 className="m-0 mb-3 text-sm font-bold text-900">{translate("reports.builder.section.annotations")}</h3>
                        <div className="flex align-items-center justify-content-between gap-2 mb-2 text-xs text-700">
                            <span>{translate("reports.builder.dataLabels.label")}</span>
                            <InputSwitch
                                checked={config.showDataLabels}
                                onChange={(event) => updateConfig({showDataLabels: event.value})}
                            />
                        </div>
                        <div className="flex align-items-center justify-content-between gap-2 mb-2 text-xs text-700">
                            <span>{translate("reports.builder.benchmark.switch")}</span>
                            <InputSwitch
                                checked={config.showBenchmark}
                                disabled={["donut", "number"].includes(config.chartStyle)}
                                onChange={(event) => updateConfig({showBenchmark: event.value})}
                            />
                        </div>
                        {["donut", "number"].includes(config.chartStyle) && (
                            <p className="m-0 mb-2 text-xs text-500">{translate("reports.builder.benchmark.disabled")}</p>
                        )}
                        {config.showBenchmark && (
                            <label className="flex flex-column gap-1 text-xs text-600">
                                <span>{translate("reports.builder.benchmark.value")}</span>
                                <InputNumber
                                    value={config.benchmarkValue}
                                    onValueChange={(event) => updateConfig({benchmarkValue: event.value})}
                                    min={0}
                                    className="w-full"
                                    inputClassName="w-full text-xs"
                                    pt={compactControlPt}
                                />
                            </label>
                        )}
                    </div>
                </div>
                <section className="col-12 lg:col-7 surface-card border-1 border-200 border-round p-3 h-full flex flex-column gap-3" aria-label={translate("reports.builder.preview")}>
                    <div className="text-center text-base font-medium text-900">
                        {config.title?.trim() || translate("reports.builder.defaultTitle")}
                    </div>

                    {context.loading && (
                        <div className="flex flex-column align-items-center justify-content-center gap-2 text-center text-xs text-500 h-20rem">
                            <ProgressSpinner className="w-2rem h-2rem"/>
                            <span>{translate("reports.builder.loadingPreview")}</span>
                        </div>
                    )}

                    {!context.loading && context.error && (
                        <Message severity="error" text={translate("reports.builder.errorPreview")} className="w-full text-xs"/>
                    )}

                    {!context.loading && !context.error && validationErrors.length > 0 && (
                        <div className="flex flex-column align-items-center justify-content-center gap-2 text-center text-xs text-500 h-20rem">
                            <i className="pi pi-info-circle text-xl"/>
                            <span>{validationErrors[0]}</span>
                        </div>
                    )}

                    {!context.loading && !context.error && !validationErrors.length && preview?.kind === "empty" && (
                        <div className="flex flex-column align-items-center justify-content-center gap-2 text-center text-xs text-500 h-20rem">
                            <i className="pi pi-database text-xl"/>
                            <span className="text-sm text-700">{translate("reports.builder.emptyPreview.title")}</span>
                            <small>{translate("reports.builder.emptyPreview.description")}</small>
                        </div>
                    )}

                    {!context.loading && !context.error && preview?.kind === "number" && (
                        <div className="flex flex-column align-items-center justify-content-center gap-2 text-center h-20rem">
                            <span className="text-6xl font-bold text-primary">{preview.value}</span>
                            <small className="text-sm text-600">{preview.label}</small>
                        </div>
                    )}

                    {!context.loading && !context.error && preview?.kind === "chart" && (
                        <div className="flex flex-column gap-2">
                            <div className="h-20rem md:h-24rem w-full">
                                <Chart
                                    type={preview.type}
                                    data={preview.data}
                                    options={chartOptions(config, translate)}
                                    className="w-full h-full"
                                />
                            </div>
                            {config.showBenchmark && config.benchmarkValue !== null && (
                                <Message
                                    severity="info"
                                    text={`${translate("reports.builder.benchmark.label")}: ${config.benchmarkValue}`}
                                    className="m-0 text-xs"
                                />
                            )}
                        </div>
                    )}

                    {config.showDataLabels && preview?.data?.datasets?.length > 0 && (
                        <div className="flex flex-wrap gap-2 justify-content-center">
                            {preview.data.labels.slice(0, 8).map((label, index) => (
                                <span className="surface-100 border-round px-2 py-1 text-xs text-600" key={label}>
                                    {label}: {preview.data.datasets[0]?.data?.[index] ?? 0}
                                </span>
                            ))}
                        </div>
                    )}
                </section>


            </div>
        </Dialog>
    );
}

export default DynamicReportBuilderDialog;
